import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { billingApi } from '../services/api';

const REASONS = [
  ['not_using', "I'm not using the product"],
  ['too_expensive', 'Too expensive'],
  ['missing_features', 'Missing features or company coverage'],
  ['difficult_to_use', 'Difficult to use'],
  ['privacy', 'Privacy concerns'],
  ['other', 'Other']
];

const DeleteAccountDialog = ({ onClose, onDeleted }) => {
  const dialog = useRef(null);
  const cancel = useRef(null);
  const submitting = useRef(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousFocus = document.activeElement;
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousOverflow = document.body.style.overflow;
    if (root) root.inert = true;
    document.body.style.overflow = 'hidden';
    cancel.current.focus();
    return () => {
      if (root) root.inert = previousInert;
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || confirmation !== 'DELETE') return;
    submitting.current = true;
    setBusy(true);
    setError('');
    dialog.current.focus();
    try {
      await billingApi.deleteAccount({
        confirmation,
        reason: reason || null,
        reason_details: reason === 'other' ? details.trim() : ''
      });
      onDeleted();
    } catch (err) {
      setError(err.message || 'Account deletion could not finish. Please retry.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" role="presentation">
      <section ref={dialog} className="confirmation-modal delete-account-dialog"
        tabIndex={-1}
        role="dialog" aria-modal="true" aria-labelledby="delete-account-title"
        aria-describedby="delete-account-warning" aria-busy={busy}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            if (!submitting.current) onClose();
          }
          if (event.key === 'Tab') {
            const controls = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')];
            if (!controls.length) { event.preventDefault(); return; }
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
            if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <h2 id="delete-account-title">Permanently delete your account?</h2>
        <p id="delete-account-warning">This is not a downgrade. Your login and personal settings will be permanently removed, and any linked paid subscriptions will be cancelled immediately. You will lose access, including Free or Client access. This cannot be undone.</p>
        <p>Deletion does not issue a refund. Shared company research and historical audit and billing records are retained.</p>
        <form className="login-form" onSubmit={submit}>
          <label htmlFor="deletion-reason">Why are you leaving? (optional)</label>
          <select id="deletion-reason" value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)}>
            <option value="">Prefer not to say</option>
            {REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          {reason === 'other' && <>
            <label htmlFor="deletion-details">Tell us more (optional)</label>
            <textarea id="deletion-details" rows={3} maxLength={2000} value={details} disabled={busy} onChange={(event) => setDetails(event.target.value)} />
          </>}
          <label htmlFor="deletion-confirmation">Type DELETE to confirm</label>
          <input id="deletion-confirmation" autoComplete="off" required value={confirmation} disabled={busy} onChange={(event) => setConfirmation(event.target.value)} />
          {error && <div className="error-box" role="alert">{error}</div>}
          {busy && <p role="status">Cancelling billing and deleting your account. Please keep this dialog open.</p>}
          <div className="modal-actions">
            <button ref={cancel} className="ghost-button" type="button" disabled={busy} onClick={onClose}>Keep my account</button>
            <button className="danger-button" type="submit" disabled={busy || confirmation !== 'DELETE'}>{busy ? 'Deleting...' : 'Permanently delete account'}</button>
          </div>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default DeleteAccountDialog;
